import * as React from "react";
import { useCopy } from '../../i18n';
import { parseUserDate, formatUserDate } from '../../utils/dateFormat';
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface DatePickerProps {
  value: Date | null | undefined;
  onChange: (date: Date | null) => void;
  onClose?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  /**
   * Esconde o icone de calendario interno. Usado quando o rotulo da linha ja
   * traz o icone (sidebar do card do Kanban). Prop em vez de seletor CSS: o
   * Button embrulha os children num <span class="contents">, entao truques do
   * tipo [&>svg:first-child]:hidden nao alcancam o icone.
   */
  hideIcon?: boolean;
  /** Identificador estável para e2e. Sem ele o teste vira refém do texto da data. */
  testId?: string;
}

/** Auto-format input as dd/MM/yyyy while typing */
function autoFormatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Leitura da data digitada. Delega para o parser canônico, que entende ISO e a
 * ordem do idioma; antes era `dd/MM/yyyy` fixo, o que lia data de en-US errado.
 */
function parseDateInput(str: string): Date | null {
  if (str.length !== 10) return null;
  return parseUserDate(str);
}

function DatePicker({
  value,
  onChange,
  onClose,
  placeholder = "Selecionar data",
  className,
  disabled = false,
  defaultOpen = false,
  hideIcon = false,
  testId,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const common = useCopy().common;

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) onClose?.();
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "group/dp w-full justify-start text-left font-normal h-8 px-2 text-sm",
            // `border-solid` explicito: global.css tem `button { border: none }`,
            // que zera o border-style herdado do preflight e faz a borda do
            // campo sumir (a classe `border` do Tailwind v3 so define a largura).
            "border border-solid border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]",
            "hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text)]",
            !value && "text-[var(--color-text-muted)]",
            className
          )}
        >
          {!hideIcon && (
            <CalendarIcon size={14} className="mr-2 shrink-0 text-[var(--color-text-muted)]" />
          )}
          {value ? (
            <span className="flex-1 truncate">
              {formatUserDate(value)}
            </span>
          ) : (
            <span className="flex-1 truncate">{placeholder}</span>
          )}
          {/* Sem × dentro do campo: mesmo invisível ele reservava 18px fixos à
              direita e o valor nunca encostava na borda, como pede o mockup.
              "Limpar" continua no rodapé do calendário. */}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[var(--color-bg)] border-[var(--color-border)]" align="start">
        <Calendar
          mode="single"
          selected={value || undefined}
          onSelect={(day) => {
            onChange(day || null);
            setOpen(false);
          }}
          defaultMonth={value || undefined}
          locale={ptBR as any}
        />
        {/* Atalhos no rodapé (spec do card, ago/2026): "Limpar" mora aqui, não
            como × permanente no campo, longe de um clique acidental. */}
        <div className="flex items-center justify-between px-3 pb-2 pt-1 text-xs border-t border-[var(--color-border)] mx-2 mt-1">
          <button
            type="button"
            className="text-[var(--color-primary)] hover:underline cursor-pointer pt-1.5"
            onClick={() => { onChange(new Date()); setOpen(false); }}
          >
            {common.project.today}
          </button>
          <button
            type="button"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] cursor-pointer pt-1.5"
            onClick={() => { onChange(null); setOpen(false); }}
          >
            {common.selection.clear}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** "YYYY-MM-DD" string → Date (noon to avoid timezone issues) */
function parseLocalDate(str: string | null | undefined): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Date → "YYYY-MM-DD" string */
function formatDateToISO(date: Date | null | undefined): string {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export { DatePicker, parseLocalDate, formatDateToISO, autoFormatDateInput, parseDateInput };
